import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  appendBreadcrumbTrail,
  buildBreadcrumbNavigatePath,
  buildBreadcrumbPathKey,
  mergeBreadcrumbIntoNavigationState,
  persistBreadcrumbTrail,
  resolveInitialBreadcrumbTrail,
} from '../utils/breadcrumbTrail';

/**
 * مدیریت breadcrumb موجودیت — پشتیبانی از refresh با sessionStorage
 */
export function useEntityBreadcrumb({
  listPath,
  listLabel,
  entityLabel,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathKey = useMemo(() => buildBreadcrumbPathKey(location.pathname), [location.pathname]);
  const entityNavigatePath = useMemo(
    () => buildBreadcrumbNavigatePath(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const [trail, setTrail] = useState(() =>
    resolveInitialBreadcrumbTrail({
      pathKey,
      locationState: location.state,
      listPath,
      listLabel,
      entityPath: entityNavigatePath,
      entityLabel: entityLabel || '…',
    }),
  );

  const trailRef = useRef(trail);
  trailRef.current = trail;

  useEffect(() => {
    const next = resolveInitialBreadcrumbTrail({
      pathKey,
      locationState: location.state,
      listPath,
      listLabel,
      entityPath: entityNavigatePath,
      entityLabel: entityLabel || '…',
    });
    setTrail(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey]);

  useEffect(() => {
    if (!entityLabel) return;
    setTrail((prev) => {
      if (!prev.length) {
        const created = [
          { path: listPath, label: listLabel },
          { path: entityNavigatePath, label: entityLabel },
        ];
        persistBreadcrumbTrail(pathKey, created);
        return created;
      }
      const last = prev[prev.length - 1];
      const matchesCurrent =
        last?.path === entityNavigatePath ||
        buildBreadcrumbPathKey(last?.path) === pathKey;
      if (matchesCurrent && last.label === entityLabel) return prev;
      const next = [...prev];
      if (matchesCurrent) {
        next[next.length - 1] = { ...last, label: entityLabel, path: entityNavigatePath };
      } else if (buildBreadcrumbPathKey(last?.path) === pathKey) {
        next[next.length - 1] = { path: entityNavigatePath, label: entityLabel };
      } else {
        next.push({ path: entityNavigatePath, label: entityLabel });
      }
      persistBreadcrumbTrail(pathKey, next);
      return next;
    });
  }, [entityLabel, entityNavigatePath, listPath, listLabel, pathKey]);

  useEffect(() => {
    if (trail.length) persistBreadcrumbTrail(pathKey, trail);
  }, [trail, pathKey]);

  const navigateWithTrail = useCallback(
    (targetPath, targetLabel, extraState = {}) => {
      const newTrail = appendBreadcrumbTrail(trailRef.current, targetPath, targetLabel);
      const targetKey = buildBreadcrumbPathKey(targetPath.split('?')[0]);
      persistBreadcrumbTrail(targetKey, newTrail);
      persistBreadcrumbTrail(targetPath, newTrail);
      navigate(targetPath, {
        state: mergeBreadcrumbIntoNavigationState(extraState, newTrail),
      });
    },
    [navigate],
  );

  const goToCrumb = useCallback(
    (index) => {
      const current = trailRef.current;
      const crumb = current[index];
      if (!crumb) return;
      const truncated = current.slice(0, index + 1);
      const crumbKey = buildBreadcrumbPathKey(crumb.path.split('?')[0]);
      persistBreadcrumbTrail(crumbKey, truncated);
      persistBreadcrumbTrail(crumb.path, truncated);
      navigate(crumb.path, {
        state: mergeBreadcrumbIntoNavigationState(location.state || {}, truncated),
      });
    },
    [navigate, location.state],
  );

  return { trail, navigateWithTrail, goToCrumb, pathKey };
}
